<template>
  <section v-if="has_children" class="invadobay-super-in">
    <button
      type="button"
      class="invadobay-super-in__title"
      :class="{ active: is_active(super_in) }"
      @click="$emit('select-child', null)"
    >
      {{ super_in.title || super_in.name }}
    </button>
    <swiper
      ref_id="invadobay-super-in-children"
      class="invadobay-super-in__children"
      :swiper_params="slider_params"
      @swiper-initialized="slide_to_active"
    >
      <swiper-slide
        v-for="child in super_in.children"
        :key="child.id"
        class="invadobay-super-in__slide"
      >
        <button
          type="button"
          class="invadobay-super-in__pill"
          :class="{ active: is_active(child) }"
          :data-text="child.title"
          @click="$emit('select-child', child)"
        >
          {{ child.title }}
        </button>
      </swiper-slide>
    </swiper>
  </section>
</template>

<script>
import { nextTick } from "vue";
import Swiper from "@components/blocks/swiper.vue";

// Upper bound for Swiper's ResizeObserver → requestAnimationFrame → resize
const RESIZE_FALLBACK_MS = 200;

export default {
  name: "invadobay-super-in",

  components: {
    swiper: Swiper,
  },

  props: {
    // { id, name, title, children, active_id }
    super_in: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      swiper_instance: null,
      slider_params: {
        classes: "invadobay-super-in__slider",
        slidesPerView: "auto",
        controls_size: 15,
        watchOverflow: true,
        hide_controls_on_lock: true,
        // Right-align the CTAs only while the track fits (locked); when it
        // overflows the slider scrolls normally (alignment off) — see swiper.vue.
        align_on_lock: "flex-end",
      },
    };
  },

  computed: {
    has_children() {
      return (
        Array.isArray(this.super_in.children) &&
        this.super_in.children.length > 0
      );
    },
  },

  watch: {
    "super_in.active_id"() {
      nextTick(() => {
        this.slide_to_active();
      });
    },
  },

  methods: {
    is_active(item) {
      const active_id = this.super_in.active_id || this.super_in.id;
      return !!item && item.id === active_id;
    },
    slide_to_active(swiper_instance) {
      if (swiper_instance) {
        this.swiper_instance = swiper_instance;
      }
      const { swiper } = this.swiper_instance || {};
      if (!swiper) {
        return;
      }
      // Slide to selected invadobay (SuperIn itself → first pill)
      try {
        const active_id = this.super_in.active_id || this.super_in.id;
        const selected_index = this.super_in.children.findIndex(
          (child) => child.id === active_id
        );

        let timeout = null;
        const slide = () => {
          clearTimeout(timeout);
          swiper.off("resize", slide);
          swiper.slideTo(Math.max(0, selected_index));
        };

        // If the slider doesn't need to resize, slide normally.
        if (!this.is_resize_pending(swiper)) {
          slide();
          return;
        }

        // Wait for the swiper to resize before sliding.
        swiper.once("resize", slide);
        timeout = setTimeout(slide, RESIZE_FALLBACK_MS);
      } catch (e) {
        this.log("invadobay-super-in.slide_to_active", e);
      }
    },
    // Checks if slider needs to resize.
    is_resize_pending(swiper) {
      const { el } = swiper;
      if (!el) {
        return false;
      }
      const style = getComputedStyle(el);
      const width =
        el.clientWidth -
        parseFloat(style.paddingLeft || 0) -
        parseFloat(style.paddingRight || 0);
      return Math.abs(width - swiper.width) >= 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.invadobay-super-in {
  $radius: $border-radius-lg * 2;

  position: absolute;
  top: 0;
  // Offsets the booking engine 1px border (positioning is relative to its padding box)
  left: -1px;
  right: -1px;
  transform: translateY(-100%);
  z-index: 100;
  display: flex;
  align-items: center;
  max-width: 1175px;
  margin: 0 auto;
  padding-top: $grid-gutter-width / 2;

  button {
    cursor: pointer;
    transition: $transition-base;

    &:focus {
      outline: none;
    }
    &:focus-visible {
      box-shadow: 0 0 0 2px var(--primary);
    }
  }

  &__title {
    flex: 0 0 auto;
    align-self: flex-end;
    min-width: 25%;
    max-width: 40%;
    margin-bottom: -1px; // covers the booking engine top border
    padding: 14px $grid-gutter-width;
    border: 1px solid $white;
    border-bottom: none;
    border-radius: $radius $radius 0 0;
    background: $extra2;
    color: $gray-500;
    font-size: $font-size-base;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover,
    &.active {
      background: $extra2;
      color: var(--primary);
      font-weight: 600;
    }
  }

  &__children {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0 0 $grid-gutter-width / 3 $grid-gutter-width;
  }

  &__slide {
    width: auto;

    &:not(:first-child) {
      margin-left: 0.75rem;
    }
  }

  &__pill {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    padding: 6px 14px;
    border: none;
    border-radius: 999px;
    background: rgba($white, 0.85);
    color: $dark;
    font-size: $font-size-md;
    line-height: normal;
    white-space: nowrap;

    // Reserves the bold width so the pill doesn't resize on hover/active
    &::after {
      content: attr(data-text);
      height: 0;
      font-weight: 600;
      visibility: hidden;
      overflow: hidden;
    }

    &:hover,
    &.active {
      background: $white;
      color: var(--primary);
      font-weight: 600;
    }
  }

  &__slider {
    // Gutters for the arrows while overflowing; flush (no gutters) once the
    // track fits (locked → arrows hidden, CTAs right-aligned).
    padding: 0 28px;

    ::v-deep swiper-container {
      // mask-image: linear-gradient(to right, transparent 0%, white 20px, white calc(100% - 20px), transparent 100%);
      mask-image: linear-gradient(to right, transparent 0%, white 0.75rem, white calc(100% - 0.75rem), transparent 100%);

      & swiper-slide {
        &.swiper-slide-active {
          margin-left: 0.75rem;
        }

        &:last-child {
          margin-right: 0.75rem;
        }
      }
    }

    &.swiper--locked {
      padding: 0;
    }
    ::v-deep .swiper__button {
      top: 50% !important;
      transform: translateY(-50%) !important;
      background: transparent !important;
      box-shadow: none !important;

      &__prev {
        left: 0 !important;

        svg {
          filter: drop-shadow(1px 0 0 $gray-900);
        }
      }

      &__next {
        right: 0 !important;

        svg {
          filter: drop-shadow(-1px 0 0 $gray-900);
        }
      }
    }

    ::v-deep .arrow {
      --color: #{$white} !important;
    }
  }
}
</style>
